<template>
	<component :is="tag" class="job-queue-candidate px-24 py-16 bg-violet-01 border flex gap-24" :data-status="candidate ? candidate.status : null">

		<svg v-if="showDragHandle" width="10" height="16" viewBox="0 0 10 16" fill="none" xmlns="http://www.w3.org/2000/svg" class="handle">
			<g opacity="0.6">
				<circle cx="1.80001" cy="1.6" r="1.6" fill="#140047" />
				<circle cx="1.80001" cy="7.9999" r="1.6" fill="#140047" />
				<circle cx="1.80001" cy="14.4003" r="1.6" fill="#140047" />
				<circle cx="8.2001" cy="1.6" r="1.6" fill="#140047" />
				<circle cx="8.2001" cy="7.9999" r="1.6" fill="#140047" />
				<circle cx="8.2001" cy="14.4003" r="1.6" fill="#140047" />
			</g>
		</svg>
		
		<div v-if="label && queueType == 'ranked'" class="show-md flex-center br-7 square-50 bg-violet-03 fs-24 lh-24 fw-700">
			{{ label }}
		</div>

		<!-- details -->
		<div class="flex gap-16">
			<ProfileAvatar :profile="profile" size="128" class="square-80"/>
			<div class="grid gap-8">
				<div class="flex gap-8">
					<div v-if="jobRole" :class="`badge badge-${jobRole.slug}`">{{ jobRole.name }}</div>
					<span v-if="ratePerHourText" :class="{'hide-md': hideRateOnDesktop}" class="fs-12 lh-16 color-violet fw-500">{{ ratePerHourText }}</span>
				</div>
				<router-link :to="{ name: $routes.Profile.name, params: { id: profile.profileId || profile.id } }" class="fs-18 lh-20 color-violet fw-700 hover-underline">
					{{ profile.name }}
				</router-link>
				<Rating :value="profile.ratingOverall" :total="profile.reviewCount"/>
				<div v-if="['activeInstantOffer', 'activeRankedOffer'].includes(this.context)" class="flex gap-20 fs-12 lh-14 mt-8">
					<router-link :to="{ name: $routes.Profile.name, params: { id: profile.id } }">	
						View Profile
					</router-link>
					<!-- <router-link :to="{ name: 'thread', params: { chatId: candidate._chatInfo.chatId, jobId: job.id } }">
						Messages
					</router-link> -->
				</div>
			</div>
		</div>


		<div class="align-right flex gap-8">
			<div v-if="context == 'findHelpQueue' && ratePerHourText" class="fs-14 lh-16 fw-400 show-md">{{ ratePerHourText }}</div>
			<div v-else :class="statusTextColor" class="fs-14 lh-16 fw-700 align-right">{{ statusText}}</div>
			<button v-if="isRemovable" class="reset rel has-tooltip color-violet op-6 hover-opaque align-right" style="padding:4px;" :title="`Remove ${profile.firstName} from the queue`" @click="$emit('remove', profile.userId)">
				<svg class="" xmlns="http://www.w3.org/2000/svg" width="16" height="18" fill="none" viewBox="0 0 16 18">
					<path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.25" d="M13.5 3.7334 2.5 3.7334M6.5 6.9331V11.1998M9.5 6.9331V11.1998M12.5 3.7334V13.8667C12.5 14.0082 12.4473 14.1438 12.3536 14.2439 12.2598 14.3439 12.1326 14.4001 12 14.4001H4C3.8674 14.4001 3.7402 14.3439 3.6465 14.2439 3.5527 14.1438 3.5 14.0082 3.5 13.8667V3.7334"/><path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.25" d="M10.5 3.73343V2.66676C10.5 2.38387 10.3946 2.11256 10.2071 1.91252C10.0196 1.71248 9.76522 1.6001 9.5 1.6001H6.5C6.23478 1.6001 5.98043 1.71248 5.79289 1.91252C5.60536 2.11256 5.5 2.38387 5.5 2.66676V3.73343"/>
				</svg>
				<Tooltip arrow="top center" class="tooltip-v2 text-center">
					<p>Remove from Queue</p>
				</Tooltip>
			</button>
		</div>
	</component>
</template>

<script>
import ChatMessageForm from '@/components/chats/ChatMessageForm';
import { JobQueue } from '@/models/Job';
import helpers from '@/mixins/helpers';

export default {
	name: 'JobQueueCandidate',
	components: {
		ChatMessageForm
	},
	mixins: [helpers],
	props: {
		canRemoveCandidates: {
			type: Boolean,
			default: true,
		},
		candidate: {
			type: Object,
			default: null,
		},
		editMode: {
			type: Boolean,
			default: false,
		},
		label: {
			type: String,
			default: null,
		},
		profile: {
			type: Object,
			required: true,
		},
		queue: {
			type: JobQueue,
			default: null,
		},
		queueType: {
			type: String,
		},
		tag: {
			type: String,
			default: 'div',
		},
		context: {
			type: String,
			default: '',
		},
	},
	computed: {
		event() {
			return this.$store.getters['events/searchEvent'];
		},
		job() {
			return this.$store.getters['singleJob/job'];
		},	
		jobRole() {
			if (this.context == 'findHelpQueue') {
				let id = this.queue.event?.jobs[0].positionId;
				return id ? this.getRoleFromId(id) : null;
			} else {
				return this.$store.getters['singleJob/role'];
			}
		},
		bookedTalentDeclined() {
			return this.$store.getters['singleJob/bookedTalentDeclined'];
		},
		showDragHandle() {
			return this.editMode && this.queueType == 'ranked' && this.context !== 'singleJobInactive';
		},
		isMovable() {
			if (!this.editMode) {
				return false;
			}
			const {
				candidate,
				queue
			} = this;
			if (!candidate || !queue) {
				return true;
			}
			const {
				order
			} = candidate;
			return (
				candidate.status === 'inQueue' &&
				(order == null || order >= queue.candidates.findIndex(({
					status
				}) => status === 'inQueue'))
			);
		},
		isRemovable() {
			return !!(this.isMovable && this.canRemoveCandidates);
		},
		ratePerHour() {
			const candidate = this.candidate;
			return candidate ? candidate.ratePerHour : null;
		},
		ratePerHourText() {
			return this.$getRatePerHourText(this.ratePerHour);
		},
		hideRateOnDesktop() {
			if (['singleJobInactive', 'completed', 'activeInstantOffer', 'activeRankedOffer'].includes(this.context)) {
				return false;
			}

			return true;
		},
		acceptedCandidateIndex() {
			return this.queue.candidates.findIndex(obj => obj.status == 'accepted');
		},
		candidateIndex() {
			return this.queue.candidates.findIndex(obj => obj.id == this.candidate.id);
		},
		statusText() {
			if (this.queue.type == 'ranked' && this.acceptedCandidateIndex > -1 && this.candidateIndex > this.acceptedCandidateIndex) {
				return 'Offer Not Sent';
			}
			switch (this.candidate?.status) {
				case 'accepted':
				case 'acceptedChanges':
					return 'Accepted';
				case 'awaitingChangesResponse':
				case 'awaitingResponse':
					return 'Awaiting Response';
				case 'declined':
				case 'declinedChanges':
				case 'declinedAfterAccepted':
				case 'declinedAfterAcceptedChanges':
				case 'employerDeclined':
					return 'Declined';
				case 'employerConfirmed':
					return 'Confirmed';
				case 'expired':
					return 'Expired';
				case 'noLongerAvailable':
					return 'No Longer Available';
				default:
					return 'In Queue';
			}
		},
		statusTextColor() {
			if (['Declined', 'Expired'].includes(this.statusText) ) {
				return 'color-red';
			}

			return 'color-green';
		}
	},
};
</script>
